// Explicit React import so consumers can render this pattern under a classic-JSX
// test runner (AlwaysAI's `tsx --test` lane); the ui/ primitives already do.
import * as React from "react";
import { useId, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { cn } from "../../lib/utils";

export type DisclosureProps = {
  title: ReactNode;
  /**
   * One line that stays readable while the section is closed. It is what stops a
   * disclosure costing a click to discover it had nothing to say.
   */
  summary?: ReactNode;
  /** Sits beside the title — a count, a state, anything short. */
  badge?: ReactNode;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /**
   * Nothing is behind this section. The header renders as a plain line instead of
   * a control, so there is nothing to open and nothing to find empty.
   */
  empty?: boolean;
  className?: string;
  children?: ReactNode;
};

const SURFACE = "rounded-xl border border-border bg-card shadow-sm";
const PADDING = "p-4";

/**
 * One titled section that opens and closes, for application layout rather than
 * for a picker.
 *
 * Controlled by design. Which section should be open is a decision about what is
 * true right now — a live window, a request waiting — and a consumer that owns
 * that rule cannot express it through a component holding its own state. It also
 * keeps persistence a per-surface choice rather than something inherited here;
 * `docs/collapse-surfaces.md` records which surfaces persist and why.
 *
 * Nothing time-critical belongs inside one of these. A countdown behind a closed
 * section is a countdown nobody reads.
 */
export function Disclosure({
  title,
  summary,
  badge,
  open,
  onOpenChange,
  empty = false,
  className,
  children,
}: DisclosureProps) {
  const titleId = useId();
  const header = <DisclosureHeader titleId={titleId} title={title} summary={summary} />;

  if (empty)
    return (
      <section className={cn(SURFACE, PADDING, className)}>
        <div className="flex items-start gap-3">
          {header}
          {badge}
        </div>
      </section>
    );

  return (
    <Collapsible
      open={open}
      onOpenChange={onOpenChange}
      className={cn("overflow-hidden", SURFACE, className)}
    >
      <CollapsibleTrigger
        className={cn(
          "flex w-full cursor-pointer items-start gap-3 text-left transition-colors hover:bg-muted",
          // The ring is inset because the trigger is flush with the card's
          // rounded edge, where an outset ring would be clipped by the
          // parent's overflow-hidden and show the keyboard user nothing.
          // Its radius follows the trigger's own, which follows the card: the
          // whole card when closed, only the top when a panel sits below.
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
          open ? "rounded-t-xl" : "rounded-xl",
          PADDING
        )}
      >
        {header}
        {badge}
        <ChevronDown
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
          aria-hidden="true"
        />
      </CollapsibleTrigger>
      <CollapsibleContent
        role="region"
        aria-labelledby={titleId}
        className={cn("border-t border-border bg-muted", PADDING)}
      >
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** The title and its summary line, identical whether or not there is a control. */
function DisclosureHeader({
  titleId,
  title,
  summary,
}: {
  titleId: string;
  title: ReactNode;
  summary?: ReactNode;
}) {
  return (
    <span className="grid flex-1 gap-1 text-left">
      <span id={titleId} className="text-sm font-semibold text-foreground">
        {title}
      </span>
      {summary ? (
        <span className="text-[13px] leading-relaxed text-muted-foreground">
          {summary}
        </span>
      ) : null}
    </span>
  );
}
